import { IRestrictions, IVariant } from "shared/hubrise/models"

/**
 * Enable or disable the item.
 * @param restrictions
 * @param enabled
 */
export function enable(restrictions: IRestrictions, enabled: boolean): IRestrictions {
  return { ...restrictions, enabled }
}

/**
 * Update restrictions after adding a variant.
 * @param variants: the new list of variants
 * @param restrictions
 * @param variantRef
 */
export function addVariant(variants: Array<IVariant>, restrictions: IRestrictions, variantRef: string): IRestrictions {
  if (restrictions.variantRefs === undefined) {
    return normaliseRestrictions(variants, restrictions)
  } else {
    const variantRefs = restrictions.variantRefs
    return normaliseRestrictions(variants, {
      ...restrictions,
      variantRefs: [...variantRefs, variantRef],
    })
  }
}

/**
 * Update restrictions after removing a variant.
 * @param variants: the new list of variants
 * @param restrictions
 * @param variantRef
 */
export function removeVariant(
  variants: Array<IVariant>,
  restrictions: IRestrictions,
  variantRef: string,
): IRestrictions {
  if (restrictions.variantRefs === undefined) {
    return normaliseRestrictions(variants, restrictions)
  } else {
    return normaliseRestrictions(variants, {
      ...restrictions,
      variantRefs: restrictions.variantRefs.filter((ref) => ref !== variantRef),
    })
  }
}

/**
 * Return true if the item is enabled. An optional variant ref can be provided.
 * @param restrictions
 * @param variantRef
 */
export function isEnabled(restrictions: IRestrictions, variantRef?: string): boolean {
  if (variantRef === undefined) {
    return restrictions.enabled
  } else {
    return restrictions.enabled && (restrictions.variantRefs ? restrictions.variantRefs.includes(variantRef) : true)
  }
}

/**
 * Return true if the item should be visible in <Preview>.
 * @param restrictions
 * @param variantRef
 */
export function isVisibleInPreview(restrictions: IRestrictions, variantRef: string | null): boolean {
  if (!restrictions.enabled) return false
  if (variantRef === null || restrictions.variantRefs === undefined) return true
  return restrictions.variantRefs.includes(variantRef)
}

/**
 * Make a variant enabled or disabled.
 * @param variants: the list of all variants
 * @param restrictions
 * @param variantRef: the variant to update
 * @param enabled
 */
export function enableVariant(
  variants: Array<IVariant>,
  restrictions: IRestrictions,
  variantRef: string,
  enabled: boolean,
): IRestrictions {
  if (enabled) {
    if (restrictions.variantRefs === undefined) {
      return restrictions
    } else {
      return normaliseRestrictions(variants, {
        ...restrictions,
        variantRefs: [...restrictions.variantRefs, variantRef],
      })
    }
  } else {
    if (restrictions.variantRefs?.length === 0) {
      return restrictions
    } else {
      const currentlyEnabledVariantRefs = restrictions.variantRefs || variants.map((v) => v.ref)
      return normaliseRestrictions(variants, {
        ...restrictions,
        variantRefs: currentlyEnabledVariantRefs.filter((ref) => ref !== variantRef),
      })
    }
  }
}

/**
 * Remove duplicate variantRefs. Return empty variantRefs if all variants are enabled.
 * @param variants
 * @param restrictions
 */
function normaliseRestrictions(variants: Array<IVariant>, restrictions: IRestrictions): IRestrictions {
  const { variantRefs } = restrictions
  if (variantRefs === undefined) return restrictions

  // Remove duplicate variantRefs and sort them
  const newVariantRefs = [...new Set(variantRefs)].sort()

  // Return empty variantRefs if all variants are enabled
  return {
    ...restrictions,
    variantRefs: newVariantRefs.length === variants.length ? undefined : newVariantRefs,
  }
}
